import React, { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";

// --- Material UI Imports for the Form ---
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  TextField,
  Stack,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";

import "../../settings.styles.css";

// --- Common Components ---
import DeleteModal from "../../../../Components/Common/DeleteModal";

// Task 24788 - read-only list, fetched directly like the LoyaltyPortal admin
// screen does. A redux slice for one dropdown would be ceremony.
import { getLoyaltySchemeListApiCall } from "../../../../helpers/fakebackend_helper";

// --- Redux Actions ---
import {
  getLoyaltySpinnerList,
  createLoyaltySpinner,
  createLoyaltySpinnerSuccess,
  updateLoyaltySpinner,
  updateLoyaltySpinnerSuccess,
  deleteLoyaltySpinner,
  deleteLoyaltySpinnerSuccess,
  getLoyaltySpinnerData,
} from "../../../../store/loyalty/action";

// --- Import the separated Preview Component ---
import LoyaltySpinnerPreview from "./LoyaltySpinnerPreview";
// Change #100251 - issue single spins, list them, void them.
import LoyaltySpinnerIssues from "./LoyaltySpinnerIssues";

// Change #100250 - links on the company's own portal address.
import { publicPortalOrigin } from "../../../../helpers/publicPortalOrigin";

// Change #100252 - "it's ok to store in seconds, but front end UX needs to be a
// dropdown for the period like hours, days, weeks, months". Still stored as
// seconds (ExpiryDurationEpoch); entered as a number and a unit. A month is 30
// days. A stored value opens in the largest unit it divides into exactly, so
// 7776000 reads as 3 months (90 days) and nothing is rounded by merely opening
// the screen.
const EXPIRY_UNITS = [
  { key: "months", label: "Months", secs: 30 * 86400 },
  { key: "weeks", label: "Weeks", secs: 7 * 86400 },
  { key: "days", label: "Days", secs: 86400 },
  { key: "hours", label: "Hours", secs: 3600 },
];
const splitExpiry = (seconds) => {
  const secs = Number(seconds) || 0;
  if (secs <= 0) return { amount: "", unit: "days" };
  const unit = EXPIRY_UNITS.find((u) => secs % u.secs === 0) || EXPIRY_UNITS[3];
  const amount = secs / unit.secs;
  return { amount: String(Number.isInteger(amount) ? amount : amount.toFixed(2)), unit: unit.key };
};

function ExpiryDurationInput({ value, onChange }) {
  const initial = splitExpiry(value);
  const [amount, setAmount] = useState(initial.amount);
  const [unit, setUnit] = useState(initial.unit);

  // Follow the stored value when it changes from outside (another spinner
  // loaded), without fighting the person typing.
  useEffect(() => {
    const secs = Number(value) || 0;
    const current = (Number(amount) || 0) * (EXPIRY_UNITS.find((u) => u.key === unit)?.secs || 1);
    if (secs !== current) {
      const next = splitExpiry(value);
      setAmount(next.amount);
      setUnit(next.unit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextAmount, nextUnit) => {
    const u = EXPIRY_UNITS.find((x) => x.key === nextUnit) || EXPIRY_UNITS[2];
    const n = Number(nextAmount);
    onChange(nextAmount === "" || !(n > 0) ? "" : String(Math.round(n * u.secs)));
  };

  return (
    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
      <TextField
        size="small"
        type="number"
        label="Voucher expires after"
        inputProps={{ min: 0, step: 1 }}
        value={amount}
        onChange={(e) => {
          setAmount(e.target.value);
          emit(e.target.value, unit);
        }}
        sx={{ flex: 1 }}
        helperText={amount === "" ? "Blank = never expires" : " "}
      />
      <Select
        size="small"
        value={unit}
        onChange={(e) => {
          setUnit(e.target.value);
          emit(amount, e.target.value);
        }}
        sx={{ minWidth: 120, alignSelf: "flex-start" }}
      >
        {EXPIRY_UNITS.map((u) => (
          <MenuItem key={u.key} value={u.key}>
            {u.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}


// ==========================================
// UTILITY FORMATTERS
// ==========================================
const formatDateTimeForInput = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.replace(" ", "T").substring(0, 16);
};

const formatDateTimeForDb = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.replace("T", " ") + ":00";
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "Ongoing";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ==========================================
// UI ICONS & COMPONENTS
// ==========================================
const ExpandIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const CopyIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

// 100% performant native color picker disguised as an MUI adornment
const isValidHex = (color) => /^#[0-9A-F]{6}$/i.test(color);

const ColorPickerInput = ({ label, value, onChange }) => {
  const safeValue = isValidHex(value) ? value : "#000000";

  return (
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      size="small"
      fullWidth
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "4px",
                overflow: "hidden",
                border: "1px solid rgba(0, 0, 0, 0.23)",
                position: "relative",
              }}
            >
              <input
                type="color"
                value={safeValue}
                onChange={onChange}
                style={{
                  position: "absolute",
                  top: -5,
                  left: -5,
                  width: 40,
                  height: 40,
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background: "none",
                }}
              />
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
};

// ==========================================
// DEFAULT STATES
// ==========================================
const generateDefaultPrize = (id, name, bgColor) => ({
  Id: id,
  RewardName: name,
  SpinnerBackgroundColour: bgColor,
  SpinnerTextColour: "#ffffff",
  // Task 24786 - ProbabilityToOneSpin is the legacy 1-in-X denominator, kept
  // so untouched spinners keep working. QtyTicketWeight is what is edited now:
  // how many of this prize are in the pool.
  ProbabilityToOneSpin: "25.00",
  QtyTicketWeight: "100",
  QtySpinnerSegment: "1",
  FlagVoucherIssue: "0",
  FlagVoucherAmount: "1",
  FlagVoucherPercentage: null,
  VouhcerAmountValue: "10.00",
  VoucherAmountPercentage: null,
  ExpiryDurationEpoch: "7776000",
  QtyVoucherMaxAllowed: "",
  QtyVoucherIssued: null,
  // Screen-only. Limited stock is off by default: quantity sets the odds and
  // the prize never runs out unless somebody asks for a cap.
  FlagLimitedStock: false,
});

const DEFAULT_PRIZES = [
  generateDefaultPrize("p1", "House Money", "#d81b60"),
  generateDefaultPrize("p2", "Gift Card", "#5e2b97"),
  generateDefaultPrize("p3", "Free Lunch", "#1e88e5"),
  generateDefaultPrize("p4", "Coffee Voucher", "#00897b"),
];

const INITIAL_FORM_STATE = {
  Id: null,
  ExternalId: "",
  Name: "SPIN TO WIN",
  Subtitle: "ANSWER A QUESTION TO WIN A PRIZE",
  MessageWinner: "You have won!",
  // Task 24788 - which loyalty scheme a signup from this spinner joins. Empty
  // means the spinner does not offer signup and the opt-in never appears.
  LoyaltyScheme_Id: "",
  // Task 24787 - staff PIN gate. StaffPin is only ever an outbound new value:
  // the server never sends one back, so blank means "leave it alone".
  FlagRequireStaffPin: false,
  // Change #100251 - only issued spins (a code on the link) can play.
  FlagRequireSpinCode: false,
  FlagStaffPinSet: false,
  StaffPin: "",
  PinUnlockQtySpins: 1,
  PinUnlockWindowMins: 5,
  ValidFromDateTime: "",
  ValidToDateTime: "",
  SpinDuration: 5,
  SpinCount: 3,
  WidgetOuterColour: "#f0f4f8",
  SpinnerOuterWheelColour: "#0d2230",
  SpinnerHeadBackgroundColour: "#a1185a",
  SpinnerCentreBackgroundColour: "#f3d37a",
  SpinnerCentreTextColour: "#464645",
  MessageUnavailableDates:
    "This loyalty promotion is no longer available to use.",
  prizes: DEFAULT_PRIZES,
};

export default function LoyaltySettingsPage() {
  const dispatch = useDispatch();

  // --- Redux State ---
  const {
    loyaltySpinnerListResponse,
    loyaltySpinnerDataResponse,
    loyaltySpinnerCreatedResponse,
    loyaltySpinnerUpdatedResponse,
    loyaltySpinnerDeletedResponse,
  } = useSelector((state) => ({
    loyaltySpinnerListResponse:
      state.Loyalty?.loyaltySpinnerList?.loyaltyspinner_list || [],
    loyaltySpinnerDataResponse: state.Loyalty?.loyaltySpinnerData,
    loyaltySpinnerCreatedResponse: state.Loyalty?.loyaltySpinnerCreatedResponse,
    loyaltySpinnerUpdatedResponse: state.Loyalty?.loyaltySpinnerUpdatedResponse,
    loyaltySpinnerDeletedResponse: state.Loyalty?.loyaltySpinnerDeletedResponse,
  }));

  // --- Page State ---
  const [selectedSpinnerId, setSelectedSpinnerId] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  // Task 24788 - the schemes a signup can join.
  const [loyaltySchemes, setLoyaltySchemes] = useState([]);

  const [expandedSegment, setExpandedSegment] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  // Change #100258 - which tab of the spinner editor is showing.
  const [spinnerTab, setSpinnerTab] = useState("design");

  // 1. Fetch List on Mount
  useEffect(() => {
    dispatch(getLoyaltySpinnerList());
  }, [dispatch]);

  // Task 24788 - and the schemes, once. A company with none simply never sees
  // anything but 'None' in the picker, which is the honest answer.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getLoyaltySchemeListApiCall();
      if (!cancelled && res?.status === 200) {
        setLoyaltySchemes(res.loyaltyscheme_list || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Handle Spinner Selection
  const handleSelectSpinner = (e) => {
    const id = e.target.value;
    setSelectedSpinnerId(id);
    setExpandedSegment(false);

    if (id === "") {
      setFormData(INITIAL_FORM_STATE);
    } else {
      dispatch(getLoyaltySpinnerData(id));
    }
  };

  // 3. Populate Form when Detailed Data arrives
  useEffect(() => {
    if (!isEmpty(loyaltySpinnerDataResponse) && selectedSpinnerId !== "") {
      const data = loyaltySpinnerDataResponse.loyaltyspinner_data;

      if (
        data &&
        data.Id &&
        data.Id.toString() === selectedSpinnerId.toString()
      ) {
        const logicData =
          data.loyaltyspinnerrewardlogic_list ||
          data.loyaltyspinnerrewardlogic_all ||
          [];

        setFormData({
          Id: data.Id,
          ExternalId: data.ExternalId || "",
          Name: data.Name || "SPIN TO WIN",
          Subtitle: data.Subtitle || "ANSWER A QUESTION TO WIN A PRIZE",
          MessageWinner: data.MessageWinner || "You have won!",
          LoyaltyScheme_Id: data.LoyaltyScheme_Id
            ? String(data.LoyaltyScheme_Id)
            : "",
          ValidFromDateTime: formatDateTimeForInput(data.ValidFromDateTime),
          ValidToDateTime: formatDateTimeForInput(data.ValidToDateTime),
          // Task 24787 - the hash never comes back; FlagStaffPinSet is how the
          // server says whether one exists.
          FlagRequireStaffPin: +(data.FlagRequireStaffPin ?? 0) === 1,
          FlagRequireSpinCode: +(data.FlagRequireSpinCode ?? 0) === 1,
          FlagStaffPinSet: +(data.FlagStaffPinSet ?? 0) === 1,
          StaffPin: "",
          PinUnlockQtySpins: parseInt(data.PinUnlockQtySpins) || 1,
          PinUnlockWindowMins: parseInt(data.PinUnlockWindowMins) || 5,
          SpinDuration: parseInt(data.SpinDurationSeconds) || 5,
          SpinCount: parseInt(data.QtyFullSpins) || 3,
          WidgetOuterColour: data.WidgetOuterColour || "#f0f4f8",
          SpinnerOuterWheelColour: data.SpinnerOuterWheelColour || "#0d2230",
          SpinnerHeadBackgroundColour:
            data.SpinnerHeadBackgroundColour || "#a1185a",
          SpinnerCentreBackgroundColour:
            data.SpinnerCentreBackgroundColour || "#f3d37a",
          SpinnerCentreTextColour: data.SpinnerCentreTextColour || "#464645",
          MessageUnavailableDates:
            data.MessageUnavailableDates ||
            "This loyalty promotion is no longer available to use.",
          prizes:
            logicData.length > 0
              ? logicData.map((item, index) => ({
                  Id: item.Id || `p_${index}`,
                  RewardName: item.RewardName || "Prize",
                  SpinnerBackgroundColour:
                    item.SpinnerBackgroundColour || "#d81b60",
                  SpinnerTextColour: item.SpinnerTextColour || "#ffffff",
                  ProbabilityToOneSpin: item.ProbabilityToOneSpin || "10.00",
                  QtyTicketWeight:
                    item.QtyTicketWeight === null ||
                    item.QtyTicketWeight === undefined
                      ? ""
                      : String(item.QtyTicketWeight),
                  QtySpinnerSegment: item.QtySpinnerSegment || "1",
                  FlagVoucherIssue: item.FlagVoucherIssue || "0",
                  FlagVoucherAmount: item.FlagVoucherAmount,
                  FlagVoucherPercentage: item.FlagVoucherPercentage,
                  VouhcerAmountValue: item.VouhcerAmountValue || "",
                  VoucherAmountPercentage: item.VoucherAmountPercentage || "",
                  ExpiryDurationEpoch: item.ExpiryDurationEpoch || "7776000",
                  QtyVoucherMaxAllowed: item.QtyVoucherMaxAllowed || "",
                  QtyVoucherIssued: item.QtyVoucherIssued,
                  // A cap already stored is what 'limited stock' means here -
                  // there is no separate flag in the database to read.
                  FlagLimitedStock: Number(item.QtyVoucherMaxAllowed) > 0,
                }))
              : DEFAULT_PRIZES,
        });
      }
    }
  }, [loyaltySpinnerDataResponse, selectedSpinnerId]);

  // --- Form Handlers ---
  const updatePrizeField = (idx, field, value) => {
    setFormData((prev) => {
      const newPrizes = [...prev.prizes];
      newPrizes[idx] = { ...newPrizes[idx], [field]: value };

      if (field === "VoucherTypeSelector") {
        if (value === "Amount") {
          newPrizes[idx].FlagVoucherAmount = "1";
          newPrizes[idx].FlagVoucherPercentage = null;
          newPrizes[idx].VoucherAmountPercentage = null;
        } else {
          newPrizes[idx].FlagVoucherAmount = null;
          newPrizes[idx].FlagVoucherPercentage = "1";
          newPrizes[idx].VouhcerAmountValue = null;
        }
      }
      return { ...prev, prizes: newPrizes };
    });
  };

  // Task 24786 - the odds, shown but never typed. Quantity in pool is the
  // input; this is the arithmetic that used to be done in somebody's head and
  // came out backwards. Recomputed on every keystroke, so a pool of
  // 1800/1200/500/173/27 reads back as 48.65% / 32.43% / 13.51% / 4.68% / 0.73%
  // while it is being typed.
  const poolTotal = formData.prizes.reduce(
    (total, p) => total + (parseFloat(p.QtyTicketWeight) || 0),
    0
  );

  const describeOdds = (prize) => {
    const qty = parseFloat(prize.QtyTicketWeight) || 0;
    if (poolTotal <= 0 || qty <= 0) return "";
    const pct = (qty / poolTotal) * 100;
    const oneIn = poolTotal / qty;
    return `${pct.toFixed(2)}% of spins (1 in ${oneIn.toFixed(1)})`;
  };

  const addPrize = () => {
    const newId = `p_${Date.now()}`;
    setFormData((prev) => ({
      ...prev,
      prizes: [
        ...prev.prizes,
        generateDefaultPrize(
          newId,
          `New Prize ${prev.prizes.length + 1}`,
          prev.prizes.length % 2 ? "#1e88e5" : "#d81b60"
        ),
      ],
    }));
    setExpandedSegment(newId);
  };

  const removePrize = (idx) => {
    setFormData((prev) => ({
      ...prev,
      prizes:
        prev.prizes.length <= 2
          ? prev.prizes
          : prev.prizes.filter((_, i) => i !== idx),
    }));
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSegment(isExpanded ? panel : false);
  };

  // --- URL Copy Handler ---
  const handleCopyUrl = () => {
    const url = `${publicPortalOrigin()}/loyalty-reward-spinner/${formData.ExternalId}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard!");
  };

  // --- Submit / Delete Handlers ---
  const handleSaveSpinner = () => {
    const segmentsList = formData.prizes.map((p) => {
      const segment = {
        RewardName: p.RewardName,
        SpinnerBackgroundColour: p.SpinnerBackgroundColour,
        SpinnerTextColour: p.SpinnerTextColour,
        ProbabilityToOneSpin: p.ProbabilityToOneSpin,
        QtyTicketWeight: p.QtyTicketWeight === "" ? null : p.QtyTicketWeight,
        QtySpinnerSegment: p.QtySpinnerSegment,
        FlagVoucherIssue: p.FlagVoucherIssue,
        FlagVoucherAmount: p.FlagVoucherAmount,
        FlagVoucherPercentage: p.FlagVoucherPercentage,
        VouhcerAmountValue: p.VouhcerAmountValue || null,
        VoucherAmountPercentage: p.VoucherAmountPercentage || null,
        ExpiryDurationEpoch: p.ExpiryDurationEpoch || null,
        // Cap only travels when the toggle is on, so turning it off genuinely
        // removes the limit rather than leaving a stale number behind.
        QtyVoucherMaxAllowed: p.FlagLimitedStock
          ? p.QtyVoucherMaxAllowed || null
          : null,
      };

      if (formData.Id && p.Id && !p.Id.toString().startsWith("p_")) {
        segment.Id = p.Id;
      }
      return segment;
    });

    const spinnerDataPayload = {
      Name: formData.Name,
      MessageWinner: formData.MessageWinner,
      LoyaltyScheme_Id: formData.LoyaltyScheme_Id || null,
      // Task 24787 - the PIN travels only when somebody typed a new one, so
      // saving the rest of the screen never disturbs the one that is set.
      FlagRequireStaffPin: formData.FlagRequireStaffPin ? 1 : 0,
      // Change #100251 - dropped server side until DatabaseDelta 184 adds it.
      FlagRequireSpinCode: formData.FlagRequireSpinCode ? 1 : 0,
      PinUnlockQtySpins: Math.max(1, parseInt(formData.PinUnlockQtySpins) || 1),
      PinUnlockWindowMins: Math.max(
        1,
        parseInt(formData.PinUnlockWindowMins) || 5
      ),
      ValidFromDateTime: formatDateTimeForDb(formData.ValidFromDateTime),
      ValidToDateTime: formatDateTimeForDb(formData.ValidToDateTime),
      FlagUseCustomQtySpinnerSegment: "1",
      SpinDurationSeconds: formData.SpinDuration.toString(),
      QtyFullSpins: formData.SpinCount.toString(),
      WidgetOuterColour: formData.WidgetOuterColour,
      SpinnerOuterWheelColour: formData.SpinnerOuterWheelColour,
      SpinnerHeadBackgroundColour: formData.SpinnerHeadBackgroundColour,
      SpinnerCentreBackgroundColour: formData.SpinnerCentreBackgroundColour,
      SpinnerCentreTextColour: formData.SpinnerCentreTextColour,
      MessageUnavailableDates: formData.MessageUnavailableDates,
      loyaltyspinnerrewardlogic_list: segmentsList,
    };

    if (formData.Id) {
      spinnerDataPayload.Id = formData.Id;
    }

    // Only send a PIN when one was typed. An empty box means "keep the PIN you
    // already have", which is the only sane reading when it cannot be shown.
    if ((formData.StaffPin || "").length === 6) {
      spinnerDataPayload.StaffPin = formData.StaffPin;
    }

    const payload = [spinnerDataPayload];

    if (formData.Id) {
      dispatch(updateLoyaltySpinner(payload));
    } else {
      dispatch(createLoyaltySpinner(payload));
    }
  };

  const handleDeleteSpinner = () => {
    if (formData.Id) {
      dispatch(deleteLoyaltySpinner([formData.Id.toString()]));
    }
  };

  // --- Success Listeners ---
  useEffect(() => {
    if (!isEmpty(loyaltySpinnerCreatedResponse)) {
      if (loyaltySpinnerCreatedResponse?.status === 200) {
        toast.success("Spinner created successfully");
        dispatch(getLoyaltySpinnerList());

        const newId = loyaltySpinnerCreatedResponse.LoyaltySpinner_Id;
        if (newId) {
          setSelectedSpinnerId(newId.toString());
          setFormData((prev) => ({ ...prev, Id: newId }));
          dispatch(getLoyaltySpinnerData(newId));
        } else {
          setFormData(INITIAL_FORM_STATE);
          setSelectedSpinnerId("");
        }
      } else {
        toast.error(
          loyaltySpinnerCreatedResponse?.message || "Error creating spinner"
        );
      }
      dispatch(createLoyaltySpinnerSuccess([]));
    }
  }, [loyaltySpinnerCreatedResponse, dispatch]);

  useEffect(() => {
    if (!isEmpty(loyaltySpinnerUpdatedResponse)) {
      if (loyaltySpinnerUpdatedResponse?.status === 200) {
        toast.success("Spinner updated successfully");
        // Task 24787 - the typed PIN has been sent and hashed; clearing it
        // stops the next save re-sending it, and marks the PIN as set.
        setFormData((prev) => ({
          ...prev,
          FlagStaffPinSet: prev.StaffPin?.length === 6 ? true : prev.FlagStaffPinSet,
          StaffPin: "",
        }));
        dispatch(getLoyaltySpinnerList());
      } else {
        toast.error(
          loyaltySpinnerUpdatedResponse?.message || "Error updating spinner"
        );
      }
      dispatch(updateLoyaltySpinnerSuccess([]));
    }
  }, [loyaltySpinnerUpdatedResponse, dispatch]);

  useEffect(() => {
    if (!isEmpty(loyaltySpinnerDeletedResponse)) {
      if (loyaltySpinnerDeletedResponse?.status === 200) {
        toast.success("Spinner deleted successfully");
        dispatch(getLoyaltySpinnerList());
        setFormData(INITIAL_FORM_STATE);
        setSelectedSpinnerId("");
        setDeleteModal(false);
      } else {
        toast.error(
          loyaltySpinnerDeletedResponse?.message || "Error deleting spinner"
        );
      }
      dispatch(deleteLoyaltySpinnerSuccess([]));
    }
  }, [loyaltySpinnerDeletedResponse, dispatch]);

  // Change #100258 - "a tab for the spinner design and a tab for issued
  // spins". Issued spins only exist for a saved spinner, so without one the
  // Design view is all there is.
  const activeSpinnerTab = formData.Id ? spinnerTab : "design";

  return (
    <>
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteSpinner}
          onCloseClick={() => setDeleteModal(false)}
        />
      )}

      <SettingsPanel
        title="Loyalty Settings"
        description="Loyalty spinner campaigns and their prizes"
      >
        {/* Change #100258 - Design / Issued spins tabs for a saved spinner. */}
        {formData.Id && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
            <Tabs
              value={activeSpinnerTab}
              onChange={(e, v) => setSpinnerTab(v)}
              sx={{ minHeight: 42 }}
            >
              <Tab
                value="design"
                label="Spinner design"
                sx={{ textTransform: "none", fontWeight: 700, minHeight: 42 }}
              />
              <Tab
                value="issues"
                label="Issued spins"
                sx={{ textTransform: "none", fontWeight: 700, minHeight: 42 }}
              />
            </Tabs>
          </Box>
        )}

        {activeSpinnerTab === "issues" && (
          <Card sx={{ mt: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                Issued spins{formData.Name ? ` - ${formData.Name}` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each issued spin is a link with its own code that can be spun
                once. Turn on "Only issued spins can play" on the Spinner design
                tab to stop the plain link working.
              </Typography>
              <LoyaltySpinnerIssues
                spinnerId={formData.Id}
                spinnerExternalId={formData.ExternalId}
              />
            </CardContent>
          </Card>
        )}

        <Row
          className="mt-3"
          style={activeSpinnerTab === "design" ? undefined : { display: "none" }}
        >
          {/* LEFT COLUMN: Editor Form */}
          <Col lg={6} xl={5}>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 0 }}>
                {/* Spinner Selector - Pinned to top */}
                <Box p={3} pb={1}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="spinner-select-label">
                      Select Spinner to Edit
                    </InputLabel>
                    <Select
                      labelId="spinner-select-label"
                      value={selectedSpinnerId}
                      label="Select Spinner to Edit"
                      onChange={handleSelectSpinner}
                      renderValue={(selected) => {
                        if (!selected) return <em>-- Create New Spinner --</em>;
                        const activeSpinner = loyaltySpinnerListResponse.find(
                          (s) => s.Id.toString() === selected.toString()
                        );
                        return activeSpinner ? (
                          activeSpinner.Name
                        ) : (
                          <em>-- Create New Spinner --</em>
                        );
                      }}
                    >
                      <MenuItem
                        value=""
                        sx={{ borderBottom: "1px solid #eee", mb: 1 }}
                      >
                        <Typography fontWeight="bold" color="primary">
                          -- Create New Spinner --
                        </Typography>
                      </MenuItem>

                      {loyaltySpinnerListResponse.map((spinner) => (
                        <MenuItem key={spinner.Id} value={spinner.Id}>
                          <Stack>
                            <Typography variant="body1" fontWeight={500}>
                              {spinner.Name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDisplayDate(spinner.ValidFromDateTime)} —{" "}
                              {formatDisplayDate(spinner.ValidToDateTime)}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box mt={3} mb={1}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {formData.Id
                        ? "Edit Properties"
                        : "New Spinner Properties"}
                    </Typography>
                  </Box>
                </Box>

                {/* ACCORDION 1: General Properties */}
                <Accordion
                  defaultExpanded
                  disableGutters
                  elevation={0}
                  sx={{
                    borderTop: "1px solid #eee",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Typography fontWeight={700}>
                      General Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {/* Copy URL UI - Only visible in Edit Mode when ExternalId exists */}
                      {formData.Id && formData.ExternalId && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1.5,
                            bgcolor: "#f0f4f8",
                            borderRadius: 1,
                            border: "1px dashed #90caf9",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: "#1976d2",
                              flexGrow: 1,
                              wordBreak: "break-all",
                            }}
                          >
                            URL: {publicPortalOrigin()}
                            /loyalty-reward-spinner/
                            {formData.ExternalId}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={handleCopyUrl}
                            sx={{ minWidth: "40px", p: 0.5, boxShadow: "none" }}
                          >
                            <CopyIcon />
                          </Button>
                        </Box>
                      )}

                      <TextField
                        label="Title"
                        value={formData.Name}
                        onChange={(e) =>
                          setFormData({ ...formData, Name: e.target.value })
                        }
                        size="small"
                        fullWidth
                      />
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label="Valid From"
                          type="datetime-local"
                          value={formData.ValidFromDateTime}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              ValidFromDateTime: e.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Valid To"
                          type="datetime-local"
                          value={formData.ValidToDateTime}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              ValidToDateTime: e.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                      <TextField
                        label="Subtitle"
                        value={formData.Subtitle}
                        onChange={(e) =>
                          setFormData({ ...formData, Subtitle: e.target.value })
                        }
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="Winner Message"
                        value={formData.MessageWinner}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            MessageWinner: e.target.value,
                          })
                        }
                        size="small"
                        fullWidth
                        helperText="Text shown on the success screen above the prize."
                      />

                      {/* Task 24788 - a spinner that captures an email may also
                          offer to sign the customer up. Which scheme that means
                          has to be said here; leave it as None and the opt-in
                          never appears on the wheel. */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Loyalty scheme for signups</InputLabel>
                        <Select
                          label="Loyalty scheme for signups"
                          value={formData.LoyaltyScheme_Id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              LoyaltyScheme_Id: e.target.value,
                            })
                          }
                        >
                          <MenuItem value="">
                            None - do not offer signup
                          </MenuItem>
                          {loyaltySchemes.map((scheme) => (
                            <MenuItem
                              key={scheme.Id}
                              value={String(scheme.Id)}
                            >
                              {scheme.Name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Change #100251 - with this on, the plain link will
                          not play: only a link carrying a spin issued below
                          (?spin=CODE), once each. */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!formData.FlagRequireSpinCode}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                FlagRequireSpinCode: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Only issued spins can play (one spin per issued code)"
                      />

                      {/* Task 24787 - the spinner page is public: anyone with
                          the link can open it. Lock it and the wheel will not
                          turn until a member of staff enters this PIN. */}
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mt: 1 }}
                      >
                        Staff PIN
                      </Typography>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!formData.FlagRequireStaffPin}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                FlagRequireStaffPin: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Staff must unlock this spinner before a customer can spin"
                      />

                      {formData.FlagRequireStaffPin && (
                        <>
                          <TextField
                            label={
                              formData.FlagStaffPinSet
                                ? "New 6-digit PIN (leave blank to keep the current one)"
                                : "6-digit PIN"
                            }
                            value={formData.StaffPin}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                // Digits only, six of them - the PIN pad on the
                                // spinner cannot type anything else.
                                StaffPin: e.target.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 6),
                              })
                            }
                            size="small"
                            fullWidth
                            inputProps={{ inputMode: "numeric" }}
                            helperText={
                              formData.FlagStaffPinSet
                                ? "A PIN is set. It is stored hashed and cannot be read back, only replaced."
                                : "No PIN is set yet. The wheel stays locked until you set one."
                            }
                          />

                          <Stack direction="row" spacing={2}>
                            <TextField
                              label="Spins per unlock"
                              type="number"
                              value={formData.PinUnlockQtySpins}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  PinUnlockQtySpins: e.target.value,
                                })
                              }
                              size="small"
                              fullWidth
                              inputProps={{ min: 1 }}
                              helperText="How many spins one correct PIN buys."
                            />
                            <TextField
                              label="Unlock lasts (minutes)"
                              type="number"
                              value={formData.PinUnlockWindowMins}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  PinUnlockWindowMins: e.target.value,
                                })
                              }
                              size="small"
                              fullWidth
                              inputProps={{ min: 1 }}
                              helperText="The unlock expires after this, spun or not."
                            />
                          </Stack>
                        </>
                      )}

                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mt: 1 }}
                      >
                        Color Configuration
                      </Typography>

                      <Stack direction="row" spacing={2}>
                        <ColorPickerInput
                          label="Widget Background"
                          value={formData.WidgetOuterColour}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              WidgetOuterColour: e.target.value,
                            })
                          }
                        />
                        <ColorPickerInput
                          label="Header & Border"
                          value={formData.SpinnerHeadBackgroundColour}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              SpinnerHeadBackgroundColour: e.target.value,
                            })
                          }
                        />
                      </Stack>

                      <Stack direction="row" spacing={2}>
                        <ColorPickerInput
                          label="Wheel Outer Rim"
                          value={formData.SpinnerOuterWheelColour}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              SpinnerOuterWheelColour: e.target.value,
                            })
                          }
                        />
                        <ColorPickerInput
                          label="Center Button Bg"
                          value={formData.SpinnerCentreBackgroundColour}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              SpinnerCentreBackgroundColour: e.target.value,
                            })
                          }
                        />
                      </Stack>

                      <ColorPickerInput
                        label="Center Button Text Color"
                        value={formData.SpinnerCentreTextColour}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            SpinnerCentreTextColour: e.target.value,
                          })
                        }
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* ACCORDION 2: Animation Settings */}
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{ borderBottom: "1px solid #eee" }}
                >
                  <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Typography fontWeight={700}>Animation Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box mb={2}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        Spin duration (seconds): {formData.SpinDuration}
                      </Typography>
                      <Slider
                        value={formData.SpinDuration}
                        onChange={(_, v) =>
                          setFormData({ ...formData, SpinDuration: v })
                        }
                        min={1}
                        max={10}
                        step={1}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        Full spins: {formData.SpinCount}
                      </Typography>
                      <Slider
                        value={formData.SpinCount}
                        onChange={(_, v) =>
                          setFormData({ ...formData, SpinCount: v })
                        }
                        min={2}
                        max={15}
                        step={1}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* ACCORDION 3: Prizes & Segments Master Area */}
                <Box p={3} bgcolor="#fafafa">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Segments ({formData.prizes.length})
                      {poolTotal > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1.5,
                            fontSize: "0.8em",
                            fontWeight: 500,
                            color: "text.secondary",
                          }}
                        >
                          Pool total: {poolTotal.toLocaleString()}
                        </Box>
                      )}
                    </Typography>
                    <Button size="small" variant="contained" onClick={addPrize}>
                      + Add Segment
                    </Button>
                  </Stack>

                  {/* Individual Segment Accordions */}
                  <Stack spacing={1}>
                    {formData.prizes.map((p, idx) => {
                      const panelId = p.Id || p.id;
                      const isOpen = expandedSegment === panelId;

                      return (
                        <Accordion
                          key={panelId}
                          expanded={isOpen}
                          onChange={handleAccordionChange(panelId)}
                          sx={{
                            border: `1px solid ${isOpen ? "#1976d2" : "#ddd"}`,
                            borderLeft: `6px solid ${p.SpinnerBackgroundColour}`,
                            borderRadius: "4px !important",
                            "&:before": { display: "none" },
                          }}
                          elevation={isOpen ? 2 : 0}
                        >
                          <AccordionSummary expandIcon={<ExpandIcon />}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              width="100%"
                              pr={1}
                            >
                              <Typography fontWeight={isOpen ? 700 : 500}>
                                {p.RewardName || "Unnamed Segment"}
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    fontSize: "0.85em",
                                    color: "text.secondary",
                                    fontWeight: "normal",
                                  }}
                                >
                                  {describeOdds(p) || "no quantity set"}
                                </Box>
                                {/* Change #100259 - how many outcomes this
                                    segment has produced. QtyVoucherIssued is
                                    counted on every win (Task 24786), capped
                                    or not, and survives a save. Saved
                                    spinners only - a new one has none yet. */}
                                {formData.Id && (
                                  <Box
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 5,
                                      fontSize: "0.75em",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      bgcolor:
                                        Number(p.QtyVoucherIssued) > 0
                                          ? "success.main"
                                          : "action.selected",
                                      color:
                                        Number(p.QtyVoucherIssued) > 0
                                          ? "success.contrastText"
                                          : "text.secondary",
                                    }}
                                  >
                                    {Number(p.QtyVoucherIssued) || 0}{" "}
                                    {Number(p.QtyVoucherIssued) === 1
                                      ? "prize issued"
                                      : "prizes issued"}
                                  </Box>
                                )}
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removePrize(idx);
                                }}
                                disabled={formData.prizes.length <= 2}
                              >
                                Delete
                              </Button>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ bgcolor: "#fff", pt: 0 }}>
                            {/* Segment Specifics */}
                            <Stack direction="row" spacing={2} mb={2} mt={1}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Prize Name"
                                value={p.RewardName}
                                onChange={(e) =>
                                  updatePrizeField(
                                    idx,
                                    "RewardName",
                                    e.target.value
                                  )
                                }
                              />
                            </Stack>

                            <Stack direction="row" spacing={2} mb={2}>
                              <ColorPickerInput
                                label="Background Color"
                                value={p.SpinnerBackgroundColour}
                                onChange={(e) =>
                                  updatePrizeField(
                                    idx,
                                    "SpinnerBackgroundColour",
                                    e.target.value
                                  )
                                }
                              />
                              <ColorPickerInput
                                label="Text Color"
                                value={p.SpinnerTextColour}
                                onChange={(e) =>
                                  updatePrizeField(
                                    idx,
                                    "SpinnerTextColour",
                                    e.target.value
                                  )
                                }
                              />
                            </Stack>

                            <Stack direction="row" spacing={2} mb={2}>
                              {/* Task 24786 - quantity in, odds out. The old
                                  field asked for a 1-in-X denominator under the
                                  label 'Win Probability (Odds)' and rendered it
                                  with a percent sign, so a prize pool typed in
                                  verbatim inverted the odds. The maths stays on
                                  screen, it is just never something you type. */}
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                label="Quantity in pool"
                                value={p.QtyTicketWeight}
                                onChange={(e) =>
                                  updatePrizeField(
                                    idx,
                                    "QtyTicketWeight",
                                    e.target.value
                                  )
                                }
                                helperText={
                                  describeOdds(p) ||
                                  "How many of this prize are in the pool"
                                }
                              />
                              <TextField
                                size="small"
                                fullWidth
                                label="Slices on Wheel (Qty)"
                                type="number"
                                value={p.QtySpinnerSegment}
                                onChange={(e) =>
                                  updatePrizeField(
                                    idx,
                                    "QtySpinnerSegment",
                                    e.target.value
                                  )
                                }
                              />
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            {/* Task 24786 - limited stock, OFF by default.
                                Ratio is the default behaviour: quantity sets the
                                odds and the prize never runs out. Lifted out of
                                the voucher-only block below because a prize can
                                run out whether or not it issues a voucher. */}
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(p.FlagLimitedStock)}
                                  onChange={(e) => {
                                    const isOn = e.target.checked;
                                    updatePrizeField(
                                      idx,
                                      "FlagLimitedStock",
                                      isOn
                                    );
                                    // Prefill with the pool quantity - '27 golden
                                    // tickets exist and 27 can be won' is the
                                    // common case - but leave it editable for
                                    // when the odds and the cap differ.
                                    if (isOn && !p.QtyVoucherMaxAllowed) {
                                      updatePrizeField(
                                        idx,
                                        "QtyVoucherMaxAllowed",
                                        p.QtyTicketWeight || ""
                                      );
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight={700}>
                                  Limited stock
                                </Typography>
                              }
                            />

                            {p.FlagLimitedStock && (
                              <Stack direction="row" spacing={2} mt={1} mb={2}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  type="number"
                                  label="Max awards"
                                  value={p.QtyVoucherMaxAllowed}
                                  onChange={(e) =>
                                    updatePrizeField(
                                      idx,
                                      "QtyVoucherMaxAllowed",
                                      e.target.value
                                    )
                                  }
                                  helperText="Stops being won once this many have gone"
                                />
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Awarded so far"
                                  value={p.QtyVoucherIssued || 0}
                                  disabled
                                  helperText="Counted by the spinner"
                                />
                              </Stack>
                            )}

                            <Divider sx={{ my: 2 }} />

                            {/* Voucher Settings */}
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={p.FlagVoucherIssue === "1"}
                                  onChange={(e) =>
                                    updatePrizeField(
                                      idx,
                                      "FlagVoucherIssue",
                                      e.target.checked ? "1" : "0"
                                    )
                                  }
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight={700}>
                                  Issue a Voucher upon winning
                                </Typography>
                              }
                            />

                            {p.FlagVoucherIssue === "1" && (
                              <Box
                                mt={2}
                                p={1.5}
                                sx={{
                                  bgcolor: "#f0f4f8",
                                  borderRadius: 1,
                                  border: "1px dashed #ccc",
                                }}
                              >
                                <Stack direction="row" spacing={2} mb={2}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Voucher Type</InputLabel>
                                    <Select
                                      label="Voucher Type"
                                      value={
                                        p.FlagVoucherAmount === "1"
                                          ? "Amount"
                                          : "Percentage"
                                      }
                                      onChange={(e) =>
                                        updatePrizeField(
                                          idx,
                                          "VoucherTypeSelector",
                                          e.target.value
                                        )
                                      }
                                    >
                                      <MenuItem value="Amount">
                                        Fixed Amount (£)
                                      </MenuItem>
                                      <MenuItem value="Percentage">
                                        Percentage (%)
                                      </MenuItem>
                                    </Select>
                                  </FormControl>

                                  {p.FlagVoucherAmount === "1" ? (
                                    <TextField
                                      size="small"
                                      fullWidth
                                      label="Value (£)"
                                      value={p.VouhcerAmountValue}
                                      onChange={(e) =>
                                        updatePrizeField(
                                          idx,
                                          "VouhcerAmountValue",
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : (
                                    <TextField
                                      size="small"
                                      fullWidth
                                      label="Percentage (%)"
                                      value={p.VoucherAmountPercentage}
                                      onChange={(e) =>
                                        updatePrizeField(
                                          idx,
                                          "VoucherAmountPercentage",
                                          e.target.value
                                        )
                                      }
                                    />
                                  )}
                                </Stack>

                                <Stack direction="row" spacing={2}>
                                  {/* Change #100252 - number + unit, stored as seconds. */}
                                  <ExpiryDurationInput
                                    value={p.ExpiryDurationEpoch}
                                    onChange={(secs) =>
                                      updatePrizeField(idx, "ExpiryDurationEpoch", secs)
                                    }
                                  />
                                </Stack>
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                </Box>

                {/* Submit Area with Delete Button */}
                <Box p={3} borderTop="1px solid #eee">
                  <Stack direction="row" spacing={2}>
                    {formData.Id && (
                      <Button
                        size="large"
                        variant="outlined"
                        color="error"
                        onClick={() => setDeleteModal(true)}
                        sx={{ borderRadius: 3, fontWeight: 900 }}
                      >
                        DELETE
                      </Button>
                    )}
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      color={formData.Id ? "warning" : "primary"}
                      onClick={handleSaveSpinner}
                      sx={{
                        borderRadius: 3,
                        py: 1.2,
                        fontWeight: 900,
                        letterSpacing: 0.5,
                      }}
                    >
                      {formData.Id ? "UPDATE SPINNER" : "CREATE SPINNER"}
                    </Button>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.7,
                      textAlign: "center",
                      display: "block",
                      mt: 1,
                    }}
                  >
                    Click the wheel on the right to test spin.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Col>

          {/* RIGHT COLUMN: Live Interactive Preview */}
          <Col lg={6} xl={7}>
            <Box sx={{ position: "sticky", top: "80px", zIndex: 10 }}>
              <LoyaltySpinnerPreview config={formData} />
            </Box>
          </Col>
        </Row>
      </SettingsPanel>
    </>
  );
}
